/**
 * Write live play look / sun back onto a catalog map file (DEV) or keep them on the
 * in-memory race clone for test-play return to the editor.
 */

import { serializeMap, type MapDocument } from '../../sim/map/index.ts';
import { canWriteRepoMap, writeRepoMap } from '../editor/persist.ts';

export type SaveLookToMapResult =
  | 'saved'
  | 'applied'
  | 'dev-only'
  | 'cancelled'
  | 'error'
  | 'no-target';

export type SaveLookToMapRequest = {
  liveMap: MapDocument;
  /** Catalog stem when racing a bundled map (`maps/<id>.json`). */
  mapId: string | null;
  testPlay: boolean;
  /** Bound editor file when test-playing (`one-stone.json`). */
  repoFile: string | null;
};

export function describeSaveLookTarget(request: SaveLookToMapRequest): string {
  if (request.mapId) {
    return `maps/${request.mapId}.json`;
  }

  if (request.testPlay) {
    return request.repoFile
      ? `editor map (${request.repoFile})`
      : 'editor working map (Back to editor)';
  }

  return 'no bound map';
}

export function saveLookSummary(): string {
  return 'Sun, water, grid, cap flock, waterline hint, under-cap rim, and aim chrome.';
}

/**
 * Writes the live race map (look + sun already on it) to the bound catalog / repo file.
 * Test-play without a repo file returns `applied` — `exitToEditor` already carries the clone.
 */
export async function saveLookToMap(
  request: SaveLookToMapRequest,
): Promise<SaveLookToMapResult> {
  const { liveMap, mapId, testPlay, repoFile } = request;

  if (!mapId && !testPlay) {
    return 'no-target';
  }

  const fileName = mapId ? `${mapId}.json` : repoFile;

  if (!fileName) {
    return testPlay ? 'applied' : 'no-target';
  }

  if (!canWriteRepoMap()) {
    return testPlay ? 'applied' : 'dev-only';
  }

  const ok = await writeRepoMap(fileName, serializeMap(liveMap));
  return ok ? 'saved' : 'error';
}
